import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Links, { NavMenu } from './Links';
import JSZip from 'jszip';
import * as cheerio from 'cheerio';
import './App.css';
import 'grapesjs/dist/css/grapes.min.css';

const ChatPlugin = () => {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    const handleInteraction = () => {
      if (!loaded) {
        setLoaded(true);
        const script = document.createElement('script');
        script.src = 'https://app.aminos.ai/js/chat_plugin.js';
        script.setAttribute('data-bot-id', '47688');
        document.body.appendChild(script);
      }
      window.removeEventListener('click', handleInteraction);
    };
    window.addEventListener('click', handleInteraction);
    return () => window.removeEventListener('click', handleInteraction);
  }, [loaded]);
  return null;
};

function App() {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  const backendUrl = 'https://xlink-app.onrender.com';

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setIsLoading(false);
        return;
      }
      try {
        const res = await axios.get(`${backendUrl}/user`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(res.data.user);
      } catch (err) {
        console.error('Auth check failed:', err.response?.status, err.response?.data);
        localStorage.removeItem('token');
        localStorage.removeItem('xlinkSeoLimit');
        setError('Session expired—please log in');
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };
    checkAuth();
  }, []);

  const showToast = (message, duration = 3000) => {
    setToast(message);
    setTimeout(() => setToast(''), duration);
  };

  const LoginForm = () => {
    const [loginEmail, setLoginEmail] = useState('');
    const [loginPassword, setLoginPassword] = useState('');
    const [regEmail, setRegEmail] = useState('');
    const [regPassword, setRegPassword] = useState('');
    const [localLoading, setLocalLoading] = useState(false);
    const [showReRegPopup, setShowReRegPopup] = useState(false);
    const [originalEmail, setOriginalEmail] = useState('');
    const navigate = useNavigate();

    const checkLocalStorage = () => JSON.parse(localStorage.getItem('xlinkSeoLimit') || '{}');

    const login = async (e) => {
      e.preventDefault();
      setError('');
      setLocalLoading(true);
      try {
        const res = await axios.post(`${backendUrl}/login`, { email: loginEmail, password: loginPassword });
        localStorage.setItem('token', res.data.token);
        setUser(res.data.user);
        showToast('Logged in');
        navigate('/dashboard');
      } catch (err) {
        setError(err.response?.data?.error || 'Login failed');
      } finally {
        setLocalLoading(false);
      }
    };

    const register = async (e) => {
      e.preventDefault();
      setError('');
      setLocalLoading(true);
      const limitData = checkLocalStorage();
      if (limitData.count > 0 && limitData.id) {
        try {
          const res = await axios.post(`${backendUrl}/user/check-email`, { localStorageId: limitData.id });
          if (res.data.email) {
            setOriginalEmail(res.data.email);
            setShowReRegPopup(true);
            return;
          }
        } catch (err) {
          console.error('Check email error:', err);
        }
      }
      try {
        const res = await axios.post(`${backendUrl}/register`, { email: regEmail, password: regPassword });
        localStorage.setItem('token', res.data.token);
        localStorage.setItem('xlinkSeoLimit', JSON.stringify({ count: 0, resetTime: Date.now() + 24 * 60 * 60 * 1000, id: res.data.user.id }));
        setUser(res.data.user);
        showToast('Registered');
        navigate('/dashboard');
      } catch (err) {
        console.error('Register error:', err.response?.data);
        setError(err.response?.data?.error || 'Registration failed');
      } finally {
        setLocalLoading(false);
      }
    };

    return (
      <div className="text-white min-h-screen flex flex-col items-center justify-center p-4">
        <h1 className="text-4xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 text-transparent bg-clip-text" style={{ fontFamily: "'Orbitron', sans-serif" }}>
          XLink SEO Generator
        </h1>
        <div className="mt-8 space-y-6 w-full max-w-md">
          <form onSubmit={login} className="space-y-4">
            <input type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} placeholder="Email" className="w-full p-3 rounded-lg bg-gray-800 border border-cyan-500 text-white focus:outline-none" autoComplete="email" name="login-email" id="login-email" required />
            <input type="password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} placeholder="Password" className="w-full p-3 rounded-lg bg-gray-800 border border-cyan-500 text-white focus:outline-none" autoComplete="current-password" name="login-password" id="login-password" required />
            <button type="submit" className="w-full bg-cyan-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-cyan-700 animate-fade-in" disabled={localLoading}>{localLoading ? 'Logging in...' : 'Log In'}</button>
          </form>
          <form onSubmit={register} className="space-y-4">
            <input type="email" value={regEmail} onChange={(e) => setRegEmail(e.target.value)} placeholder="Email" className="w-full p-3 rounded-lg bg-gray-800 border border-cyan-500 text-white focus:outline-none" autoComplete="email" name="reg-email" id="reg-email" required />
            <input type="password" value={regPassword} onChange={(e) => setRegPassword(e.target.value)} placeholder="Password" className="w-full p-3 rounded-lg bg-gray-800 border border-cyan-500 text-white focus:outline-none" autoComplete="new-password" name="reg-password" id="reg-password" required />
            <button type="submit" className="w-full bg-green-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-green-700 animate-fade-in" disabled={localLoading}>{localLoading ? 'Signing up...' : 'Sign Up (2 Free Runs)'}</button>
          </form>
        </div>
        {error && <p className="text-red-400 mt-6 text-center">{error}</p>}
        {showReRegPopup && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-gray-800 p-6 rounded-lg text-white">
              <p>Registered as {originalEmail}—Log In or <a href="#" className="text-cyan-400">Forgot Password</a></p>
              <button onClick={() => setShowReRegPopup(false)} className="bg-cyan-600 text-white px-4 py-2 mt-4 hover:bg-cyan-700">Close</button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const Dashboard = () => {
    const [url, setUrl] = useState('');
    const [services, setServices] = useState('');
    const [locations, setLocations] = useState('');
    const [backlinkSwap, setBacklinkSwap] = useState(false);
    const [results, setResults] = useState(null);
    const [localLoading, setLocalLoading] = useState(false);
    const navigate = useNavigate();

    const logout = () => {
      localStorage.removeItem('token');
      localStorage.removeItem('xlinkSeoLimit');
      setUser(null);
      showToast('Logged out');
      navigate('/');
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      setError('');
      setResults(null);
      if (!user) {
        setError('Please log in.');
        navigate('/');
        return;
      }
      if (user.runsLeft <= 0) {
        setError('No runs left—subscribe for more!');
        return;
      }
      setLocalLoading(true);
      try {
        const token = localStorage.getItem('token');
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const analysisRes = await axios.post(`${backendUrl}/analyze`, { url }, config);
        const analysis = analysisRes.data;

        const generateRes = await axios.post(`${backendUrl}/generate`, {
          url,
          services,
          locations,
          fullHtml: analysis.fullHtml,
          backlinkSwap
        }, { ...config, responseType: 'blob' });

        const downloadUrl = window.URL.createObjectURL(generateRes.data);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', 'seo-pages.zip');
        document.body.appendChild(link);
        link.click();
        link.remove();

        const userRes = await axios.get(`${backendUrl}/user`, config);
        setUser(userRes.data.user);
        showToast('Download started!');
        setResults(analysis);
      } catch (err) {
        if (err.response?.status === 401) {
          setError('Unauthorized—please log in again.');
          logout();
        } else if (err.response?.status === 403) setError('Site blocked or restricted (HTTP 403).');
        else if (err.response?.status === 503) setError('Site down—try later.');
        else if (err.response?.status === 404) setError('Site not found.');
        else setError(err.response?.data?.error || `Something went wrong: ${err.message}`);
      } finally {
        setLocalLoading(false);
      }
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 to-purple-900 flex flex-col items-center p-4">
        <div className="w-full max-w-md">
          <NavMenu onLogout={logout} />
          <h1 className="text-4xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 text-transparent bg-clip-text text-center mb-6" style={{ fontFamily: "'Orbitron', sans-serif" }}>
            Dashboard
          </h1>
          <p className="mt-4 text-center">Runs Left: {user ? user.runsLeft : 'Loading...'}</p><br/>
          <h6 className="text-1xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 text-transparent bg-clip-text text-center mb-6" style={{ fontFamily: "'Orbitron', sans-serif" }}>
            1000 Pages Maximum Per Run</h6>
            <form onSubmit={handleSubmit} className="space-y-6 mt-8">
            <input type="text" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://example.com" className="w-full p-3 rounded-lg bg-gray-800 border border-cyan-500 text-white focus:outline-none" name="url" id="url" required />
            <input type="text" value={services} onChange={(e) => setServices(e.target.value)} placeholder="services, products, offers" className="w-full p-3 rounded-lg bg-gray-800 border border-cyan-500 text-white focus:outline-none" name="services" id="services" required />
            <input type="text" value={locations} onChange={(e) => setLocations(e.target.value)} placeholder="cities, states, countries" className="w-full p-3 rounded-lg bg-gray-800 border border-cyan-500 text-white focus:outline-none" name="locations" id="locations" required />
            <label className="flex items-center">
              <input type="checkbox" checked={backlinkSwap} onChange={(e) => setBacklinkSwap(e.target.checked)} className="mr-2" name="backlink-swap" id="backlink-swap" /> Backlink Swap
            </label>
            <button type="submit" className="w-full bg-cyan-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-cyan-700 animate-fade-in" disabled={localLoading}>
              {localLoading ? 'Processing...' : 'Generate'}
            </button>
          </form>
          {error && <p className="text-red-400 mt-6 text-center">{error}</p>}
          {results && (
            <div className="mt-8 animate-fade-in">
              <p>Title: {results.title}</p>
              <p>Description: {results.description}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const History = () => {
    const navigate = useNavigate();
    const [history, setHistory] = useState(user?.history || []);
    const [selectedZip, setSelectedZip] = useState(null);

    useEffect(() => {
      if (!user) navigate('/');
      else setHistory(user.history);
    }, [user, navigate]);

    const refreshToken = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${backendUrl}/user`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(res.data.user);
        return token;
      } catch (err) {
        localStorage.removeItem('token');
        localStorage.removeItem('xlinkSeoLimit');
        setUser(null);
        navigate('/');
        throw new Error('Session expired');
      }
    };

    const logout = () => {
      localStorage.removeItem('token');
      localStorage.removeItem('xlinkSeoLimit');
      setUser(null);
      showToast('Logged out');
      navigate('/');
    };

    const downloadZip = async (id) => {
      try {
        let token = localStorage.getItem('token');
        if (!token) throw new Error('No token');
        let res;
        try {
          res = await axios.get(`${backendUrl}/history/${id}/zip`, {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob'
          });
        } catch (err) {
          if (err.response?.status === 401) {
            token = await refreshToken();
            res = await axios.get(`${backendUrl}/history/${id}/zip`, {
              headers: { Authorization: `Bearer ${token}` },
              responseType: 'blob'
            });
          } else {
            throw err;
          }
        }
        const downloadUrl = window.URL.createObjectURL(res.data);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', `seo-pages-${id}.zip`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        showToast('Download started!');
      } catch (err) {
        setError('Failed to load ZIP - possibly session expired');
      }
    };

    const loadZip = async (id) => {
      try {
        let token = localStorage.getItem('token');
        if (!token) throw new Error('No token');
        let res;
        try {
          res = await axios.get(`${backendUrl}/history/${id}/zip`, {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'arraybuffer'
          });
        } catch (err) {
          if (err.response?.status === 401) {
            token = await refreshToken();
            res = await axios.get(`${backendUrl}/history/${id}/zip`, {
              headers: { Authorization: `Bearer ${token}` },
              responseType: 'arraybuffer'
            });
          } else {
            throw err;
          }
        }
        const zip = await JSZip.loadAsync(res.data);
        const files = {};
        await Promise.all(Object.keys(zip.files).map(async (filename) => {
          if (filename.endsWith('.html')) {
            files[filename] = await zip.files[filename].async('string');
          }
        }));
        setSelectedZip({ id, files });
      } catch (err) {
        setError('Failed to load ZIP - possibly session expired');
        console.error('ZIP load error:', err.message);
      }
    };

    const openEditorTab = async (id, filename) => {
      try {
        let token = localStorage.getItem('token');
        if (!token) throw new Error('No token');
        let res;
        try {
          res = await axios.get(`${backendUrl}/history/${id}/zip`, {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'arraybuffer'
          });
        } catch (err) {
          if (err.response?.status === 401) {
            token = await refreshToken();
            res = await axios.get(`${backendUrl}/history/${id}/zip`, {
              headers: { Authorization: `Bearer ${token}` },
              responseType: 'arraybuffer'
            });
          } else {
            throw err;
          }
        }
        const zip = await JSZip.loadAsync(res.data);
        const content = await zip.files[filename].async('string');
        const $ = cheerio.load(content);
        $('meta[http-equiv="Content-Security-Policy"]').remove();
        const fullHtml = $.html();

        const editorWindow = window.open('https://xlinkseo.com/page-editor', '_blank');
        setTimeout(() => {
          editorWindow.postMessage({
            type: 'init',
            fullHtml,
            id,
            filename,
            token
          }, 'https://xlinkseo.com');
        }, 1000);

        window.addEventListener('message', (e) => {
          if (e.data === 'save-success') {
            refreshToken().then(() => {
              setHistory(user.history);
              setSelectedZip(null);
              showToast('Changes saved to history!');
            });
          }
        });
      } catch (err) {
        setError('Failed to load ZIP - possibly session expired');
        console.error('ZIP load error:', err.message);
      }
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 to-purple-900 flex flex-col items-center p-4">
        <div className="w-full max-w-4xl">
          <NavMenu onLogout={logout} />
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 text-transparent bg-clip-text" style={{ fontFamily: "'Orbitron', sans-serif" }}>
              History
            </h1>
            <button 
              onClick={() => navigate('/dashboard')} 
              className="text-cyan-400 hover:text-cyan-500 font-semibold px-4 py-2 rounded-lg border border-cyan-500 hover:border-cyan-600"
            >
              Back to Dashboard
            </button>
          </div>
          {history.length === 0 ? (
            <p>No history yet—generate some pages!</p>
          ) : (
            <table className="w-full text-left">
              <thead>
                <tr><th>URL</th><th>Date</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {history.map((entry) => (
                  <tr key={entry.id}>
                    <td>{entry.url}</td>
                    <td>{new Date(entry.generated_at).toLocaleString()}</td>
                    <td>
                      <button onClick={() => downloadZip(entry.id)} className="text-cyan-400 hover:text-cyan-500 mr-2">Download</button>
                      <button onClick={() => loadZip(entry.id)} className="text-cyan-400 hover:text-cyan-500">Edit</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {selectedZip && (
            <div className="mt-8 animate-fade-in">
              <h2 className="text-2xl">Files in ZIP {selectedZip.id}</h2>
              <ul className="mt-4">
                {Object.keys(selectedZip.files).map((filename) => (
                  <li key={filename}>
                    <button 
                      onClick={() => openEditorTab(selectedZip.id, filename)} 
                      className="text-cyan-400 hover:text-cyan-500"
                    >
                      Edit {filename}
                    </button>
                  </li>
                ))}
              </ul>
              <button 
                onClick={() => setSelectedZip(null)} 
                className="mt-4 text-cyan-400 hover:text-cyan-500"
              >
                Close
              </button>
            </div>
          )}
          {error && <p className="text-red-400 mt-6 text-center">{error}</p>}
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <p className="text-white text-xl">Loading...</p>
      </div>
    );
  }

  return (
    <Router>
      <ChatPlugin />
      {toast && (
        <div className="fixed top-4 right-4 bg-cyan-600 text-white px-4 py-2 rounded-lg shadow-lg animate-fade-in">
          {toast}
          <button onClick={() => setToast('')} className="ml-2 text-sm">✕</button>
        </div>
      )}
      <Routes>
        <Route path="/" element={user ? <Navigate to="/dashboard" /> : <LoginForm />} />
        <Route path="/dashboard" element={user ? <Dashboard /> : <Navigate to="/" />} />
        <Route path="/history" element={user ? <History /> : <Navigate to="/" />} />
        <Route path="/links" element={user ? <Links user={user} setUser={setUser} showToast={showToast} /> : <Navigate to="/" />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Router>
  );
}

export default App;
